import { type AiTagId } from '@solinkcorp/api-types';
import { type UserSession } from '@solinkcorp/authentication';
import { mutate } from 'swr';
import { v5 as uuidv5 } from 'uuid';
import type { AiTag, AiTagWithCategory } from './useAiTags';

export const AI_TAGS_KEY = '/api/ai-tags';

/**
 * Separate SWR cache for optimistically-added typed-tag placeholders. Lives
 * outside AI_TAGS_KEY so `useSaveAiTags`'s "already persisted" filter (which
 * reads `useCustomerAiTags`) doesn't see placeholders and skip POSTing them.
 */
export const AI_TAG_PLACEHOLDERS_KEY = 'ai-tag-placeholders';

export const CUSTOM_CATEGORY = 'Custom' as const;

export interface AiTagOption {
  readonly label: string;
  readonly value: string;
}

export interface AiTagOptionGroup {
  readonly label: string;
  readonly options: readonly AiTagOption[];
}

// Fixed namespace for deriving stable client-side UUIDs from AI tag names.
// The id is derived from `${customerId}:${name}` so the same tag name produces
// a different UUID across customers — otherwise customers collide on the same
// synthetic id before the tag round-trips through useSaveAiTags, and one
// customer's typed tag can clobber another's in shared SWR caches.
const AI_TAG_NAMESPACE = '8c3a4e1f-2b6d-4f7a-9e5c-1a8b3d6f9c2e';

export function aiTagIdFromName(name: string, customerId: string): string {
  return uuidv5(`${customerId}:${name.toLowerCase()}`, AI_TAG_NAMESPACE);
}

export function remapAiTagIds(ids: readonly AiTagId[], idRemap: Map<AiTagId, AiTagId>): AiTagId[] {
  if (idRemap.size === 0) return [...ids];
  return ids.map((id) => idRemap.get(id) ?? id);
}

/**
 * Optimistically seeds freshly-typed AI tag placeholders so `useAiTags` can
 * resolve the new id → name mapping immediately, before `useSaveAiTags` POSTs
 * the tag. The caller has already derived a stable v5 id from the typed name
 * (see `aiTagIdFromName`).
 *
 * Placeholders live in a separate SWR cache from `useCustomerAiTags` on
 * purpose — `useSaveAiTags` filters out already-persisted customer tags by
 * checking that cache, so co-mingling placeholders there would cause typed
 * tags to be silently skipped instead of POSTed.
 */
export async function addPlaceholderToCache(
  newTags: ReadonlyArray<{ id: AiTagId; name: string }>,
  user: UserSession,
): Promise<void> {
  if (newTags.length === 0) return;

  const placeholders: AiTag[] = newTags.map(({ id, name }) => ({
    id,
    name,
    cloudId: user.jwtUser.app_metadata.cloudId,
    resellerId: user.jwtUser.app_metadata.resellerId,
    customerId: user.customerId,
  }));

  await mutate(
    AI_TAG_PLACEHOLDERS_KEY,
    (currentData: AiTag[] | undefined) => {
      const existing = currentData ?? [];
      const filtered = existing.filter((tag) => !placeholders.some((p) => p.id === tag.id));
      return [...filtered, ...placeholders];
    },
    { revalidate: false },
  );
}

/**
 * Groups merged AI tags by category for picker rendering. Custom is pinned
 * first; remaining categories sorted A–Z; tag names sorted alphanumerically
 * within each group.
 */
export function groupAiTagsByCategory(tags: readonly AiTagWithCategory[]): AiTagOptionGroup[] {
  const byCategory = new Map<string, AiTagWithCategory[]>();
  for (const tag of tags) {
    const existing = byCategory.get(tag.category);
    if (existing) existing.push(tag);
    else byCategory.set(tag.category, [tag]);
  }

  const sortedCategories = Array.from(byCategory.keys()).sort((a, b) => {
    if (a === CUSTOM_CATEGORY) return -1;
    if (b === CUSTOM_CATEGORY) return 1;
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  });

  return sortedCategories.map((category) => ({
    label: category,
    options: byCategory
      .get(category)!
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
      )
      .map((t) => ({ label: t.name, value: t.id })),
  }));
}

/**
 * Returns true if `name` is usable as a new AI tag — non-empty and not already
 * present in any group. Use as the `isValidNewOption` handler for creatable
 * selects to gate the "Create X" prompt.
 */
export function isValidNewAiTagName(name: string, groups: readonly AiTagOptionGroup[]): boolean {
  if (name.trim() === '') return false;
  return !groups.some((group) => group.options.some((option) => option.label === name));
}
